import { useQuery } from '@tanstack/react-query';
import { useState, useEffect } from 'react';
import api from '../services/api';

export interface CatalogSearchResult {
  catalog_product_id: string;
  normalized_url: string;
  name: string | null;
  retailer: string | null;
  image_url: string | null;
  current_best_price: number | null;
  currency: string | null;
  // Relevance scoring — surfaced on each card in the internal AWL UI
  // so we can see why ranking landed where it did.
  name_score?: number;
  search_score?: number;
  q_total?: number;
  match_fraction?: number;
  // Vector hybrid path only — null/undefined when the request used the
  // text-only path. vector_similarity in [0,1] (1 = identical embedding);
  // hybrid_score is the weighted combine that drove the actual ranking.
  vector_similarity?: number;
  hybrid_score?: number;
}

export interface CatalogSearchExpansion {
  /** Original query, post-normalization. */
  original: string;
  /** Terms the LLM expander added (empty if no expansion or expander off). */
  added: string[];
  /** True if the expansion came from the cache, false if Gemini was called. */
  cached: boolean;
  /** Set when the expander failed; search ran on the original query alone. */
  error?: string;
}

export interface CatalogSearchResponse {
  results: CatalogSearchResult[];
  /** Null when EXPAND_QUERIES is off in the backend. */
  expansion: CatalogSearchExpansion | null;
  /** True when the request was served by the hybrid text+vector path
   * (VECTOR_SEARCH_ENABLED in the backend). Drives the badge layout. */
  hybrid?: boolean;
}

function useDebounce<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

export function useCatalogSearch(query: string, limit: number = 20) {
  const debouncedQuery = useDebounce(query.trim(), 500);

  return useQuery({
    queryKey: ['catalogSearch', debouncedQuery, limit],
    queryFn: async (): Promise<CatalogSearchResponse> => {
      const res = await api.get<{
        success: boolean;
        data: CatalogSearchResult[];
        expansion?: CatalogSearchExpansion | null;
        hybrid?: boolean;
      }>('/products/search', { params: { q: debouncedQuery, limit } });
      return {
        results: res.data.data,
        expansion: res.data.expansion ?? null,
        hybrid: res.data.hybrid ?? false,
      };
    },
    enabled: debouncedQuery.length >= 2,
    placeholderData: (prev: CatalogSearchResponse | undefined) => prev,
    staleTime: 30_000,
  });
}
